import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
  createContext,
} from "react";
import { loadSpace, loadEmbed } from "@usersnap/browser";
import { getInfo as getDatadogInfo } from "@labxchange/util-datadog";

const USERSNAP_SPACE_API_KEY = process.env.REACT_APP_USERSNAP_SPACE_API_KEY;

export const UsersnapContext = createContext({
  usersnapApi: null,
  isActive: false,
  loadUsersnap: async () => null,
});

export const UsersnapProvider = ({
  initParams = null,
  onLoadCallback,
  children,
  lazy = false,
}) => {
  const [usersnapApi, setUsersnapApi] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const isInitializingRef = useRef(false);
  const onLoadCallbackRef = useRef(onLoadCallback);

  // Keep onLoadCallback ref in sync without causing re-renders
  useEffect(() => {
    onLoadCallbackRef.current = onLoadCallback;
  }, [onLoadCallback]);

  // Usersnap injects <us-button> and <us-widget> custom elements into <body> with no
  // ARIA role on the host, causing screen readers to announce them as "group".
  // Setting role="none" on the host removes that spurious wrapper from the a11y tree
  // so only the shadow-DOM content (the actual button or dialog) is exposed.
  // Note: this is a best-effort fix from our side. Chrome requires the element author
  // to set the role via ElementInternals for it to take full effect on custom elements —
  // a separate ticket has been raised with Usersnap to fix this at the source.
  useEffect(() => {
    const patch = (el) => {
      if (!el.hasAttribute("role")) {
        el.setAttribute("role", "none");
      }
    };

    document.querySelectorAll("us-button, us-widget").forEach(patch);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.tagName === "US-BUTTON" || node.tagName === "US-WIDGET")
          ) {
            patch(node);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  const initializeUsersnap = useCallback(async () => {
    if (usersnapApi || isInitializingRef.current) return usersnapApi;

    if (!USERSNAP_SPACE_API_KEY) {
      console.error("USERSNAP_SPACE_API_KEY is missing");
      return null;
    }

    isInitializingRef.current = true;
    console.info("initializing usersnap");

    try {
      const api = await loadSpace(USERSNAP_SPACE_API_KEY);

      api.init({ init: initParams, custom: getDatadogInfo() });

      api.on("open", () => setIsActive(true));
      api.on("close", () => setIsActive(false));
      api.on("load", (event) => onLoadCallbackRef.current?.(event, api));

      setUsersnapApi(api);
      return api;
    } catch (error) {
      console.error("Failed to load Usersnap", error);
      isInitializingRef.current = false;
      return null;
    }
  }, [initParams, usersnapApi]);

  // Default behavior: load on mount if not lazy
  useEffect(() => {
    if (!lazy) {
      initializeUsersnap();
    }
  }, [lazy, initializeUsersnap]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      usersnapApi?.destroy();
    };
  }, [usersnapApi]);

  return (
    <UsersnapContext.Provider
      value={{
        usersnapApi,
        isActive,
        loadUsersnap: initializeUsersnap,
      }}
    >
      {children}
    </UsersnapContext.Provider>
  );
};

// Enhanced hook with lazy loading support
export const useUsersnap = () => {
  const context = useContext(UsersnapContext);
  if (!context) {
    throw new Error("useUsersnap must be used within a UsersnapProvider");
  }

  return {
    api: context.usersnapApi,
    isActive: context.isActive,
    loadUsersnap: context.loadUsersnap,
    ensureLoaded: async () => {
      if (context.usersnapApi) return context.usersnapApi;
      return await context.loadUsersnap();
    },
  };
};

/**
 * Load and mount an embedded Usersnap widget to a specific DOM element.
 * All filtering (environment, language, URL, etc.) is handled by Usersnap's configuration.
 *
 * @param {HTMLElement} element - The DOM element to mount the widget to
 * @param {string|Object} widgetKey - The Usersnap widget (string ID or widget object)
 * @param {Object} options - Optional configuration options for the widget
 *
 * @example
 * // With widget constant
 * usersnapLoadEmbededWidget(
 *   document.getElementById('feedback-container'),
 *   USERSNAP_WIDGETS.SCROLLABLE_SATISFACTION_SCORE
 * )
 *
 * @example
 * // With custom options
 * usersnapLoadEmbededWidget(
 *   document.getElementById('feedback-container'),
 *   USERSNAP_WIDGETS.SCROLLABLE_SATISFACTION_SCORE,
 *   { valuesBlacklist: ['password'] }
 * )
 */
export function usersnapLoadEmbededWidget(element, widgetKey, options = {}) {
  if (!widgetKey) {
    console.warn("[Usersnap] No widget key provided for embedded widget");
    return;
  }

  if (!element) {
    console.warn("[Usersnap] No mount element provided for embedded widget");
    return;
  }

  // Extract ID if widgetKey is an object, otherwise use as-is
  const widgetId = typeof widgetKey === "object" ? widgetKey.id : widgetKey;

  loadEmbed(widgetId)
    .then((api) => {
      api.init({
        mountNode: element,
        ...options,
      });
    })
    .catch((error) => {
      console.error("[Usersnap] Failed to load embedded widget:", error);
    });
}

export function useUsersnapApi() {
  return useContext(UsersnapContext).usersnapApi;
}

export function isUsersnapActive() {
  return useContext(UsersnapContext).isActive;
}
